/**
 * VerbForm (13-25): the one form every record panel uses to save a register verb. Config-driven: it names no column.
 *
 * - **Renders** the verb's columns from the field set by kind (`lib/verbForm`): a choice shows the live values
 *   verbatim with a visible "Not answered" state and nothing pre-selected (a blank is never an answer, FIELDSET
 *   §1.1); a text area 4,000 with a count, a text 255; a day is the date input's own `YYYY-MM-DD` text, written by
 *   `fieldValues` per §1.4; an instant is a date and a time, Melbourne (D-13-L-10); a person is a Harmony address
 *   with "Me"; a position is plain text, "a role, not a name" (§1.5).
 * - **Prefills** from the record, and sends the engine only the columns whose input changed. The engine (13-20's
 *   `buildRecordVerb`, or the builder a panel passes) decides the patch, the stamps, `Status` and the sibling rows,
 *   and every refusal comes back at its field.
 * - **Confirms** every change in plain words ("Status: New → Actioning"), then saves synchronously through
 *   `writer.commitRecordVerb` (D-13-L-4: no outbox, no toast undo). While saving the form is inert.
 * - **After:** a quiet tick and "Saved" (or "Saved; N audit rows will be posted", D-13-L-5), then `onSaved`. A 412
 *   shows the fresh value beside each conflicting field and asks again; nothing is merged (D-13-L-4).
 * - **13-26 extras** (closure panels): inputs that are not columns (`extraFields`: the close day, the next review,
 *   the review text), placed with the refusals for the column they stand for; a required reason (`reasonPrompt`);
 *   a note in the confirm (`confirmNote`, e.g. the open Closure Links), and where a refused column is recorded when
 *   it is another panel's (`elsewhere`). The save tone is teal, or charcoal for cancel and reopen (`saveTone`).
 * - **13-27 extras** (the regulatory panels): columns that start blank whatever the record holds (`unprefilled`:
 *   nothing pre-selected), a control a panel draws itself (`renderControl`: the neutral option cards), columns shown
 *   only for some answers (`showColumn`: a hidden column is neither shown nor sent), a live block after the fields
 *   (`afterFields`: the computed deadline and its override) and a note under one field (`fieldNote`: a notice's due
 *   line), both with the form's controls (`VerbFormControl`), and start values where the record is blank (`defaults`:
 *   the notifier is the signed-in person).
 * - **13-28 extra:** a block before a field (`beforeField`: the WHS panel's numbered group headings).
 * - **13.2-10 extras** (close as a duplicate): a one-line text input (`kind: 'text'`) whose value the panel follows as
 *   it is typed (`onExtraChange`: the kept record is read from it), a key that sends the form back to editing when
 *   something it doesn't own changes (`resetKey`: the kept record's read landing), and rows the save owes another
 *   record (`afterCommit`: the kept record's Note), posted after the save landed and never able to undo it.
 * Practice mode: the shell's ribbon shows, and the runtime's writer is the practice one (nothing is written).
 */

import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Input, Select, TextArea } from '../ui';
import { choicesOf } from '../../config/fieldset/registerFields';
import { useActionRuntime } from '../../hooks/useActionRuntime';
import { formatDay, formatInstant, melbourneWallToInstant, todayMelbourne } from '../../lib/fieldValues';
import { RECORD_AUTHORITY_TEXT } from '../../lib/recordAuthority';
import type { RecordAuthorityContext } from '../../lib/recordAuthority';
import { ENGINE_VERBS, buildRecordVerb } from '../../lib/recordVerbs';
import type { EngineVerb, RecordVerbBuild, SiblingInput } from '../../lib/recordVerbs';
import {
  NOT_RECORDED,
  changeLines,
  changedSet,
  clearedColumns,
  displayValue,
  formRefusals,
  prefill,
  specOf,
  stampLabels,
  takesTime,
  timeKeyOf,
  verbInputColumns,
} from '../../lib/verbForm';
import type { ChangeLine, FormValues } from '../../lib/verbForm';
import { RecordConflictError, RecordRefusedError, preparedByUpn } from '../../services/recordService';
import type { RecordIO, SiblingRow } from '../../services/recordService';
import type { WriteMode } from '../../services/commitAdapter';
import type { Actor } from '../../types/actionRegister';
import type { RecordPatch, RecordRegister, RecordRow, RecordVerbKey, Refusal, VerbResult } from '../../types/records';
import styles from './record.module.css';

/** What the builder is given: the changed columns, plus what the panel adds. */
export interface VerbBuildInput {
  set: Readonly<Record<string, unknown>>;
  reason?: string;
  rows?: readonly SiblingInput[];
  /** The `extraFields` values, by key (13-26). */
  extra?: Readonly<Record<string, string>>;
  auditId: string;
}

/** An input that is not a column (13-26): a day or a text the builder takes under its own key. */
export interface VerbExtraField {
  key: string;
  label: string;
  /** 13.2-10: `text` is a one-line input (the kept record's ref). */
  kind: 'day' | 'textarea' | 'text';
  /** The column (or refusal key) its refusals carry, so they show here. Default: `key`. */
  column?: string;
  initial?: string;
  hint?: string;
}

/** A required reason the form always asks for (cancel, reopen, retire). */
export interface VerbReasonPrompt {
  label: string;
  hint?: string;
}

/** What `afterFields` and `fieldNote` may do to the form (13-27). */
export interface VerbFormControl {
  /** Set one input (a column, or `<column>@time`), as typing would. */
  set: (column: string, value: string) => void;
  /** Something the builder reads outside the inputs changed (the override): back to editing, nothing reviewed. */
  invalidate: () => void;
  /** The refusal placed at a key (e.g. `dueOverride`), if any. */
  refusal: (key: string) => string | undefined;
}

/** What a panel's own control is given (13-27 `renderControl`). */
export interface VerbControlProps {
  id: string;
  value: string;
  set: (value: string) => void;
  refused: boolean;
  describedBy?: string;
  /** Every input as it stands, and the form's controls (to fill a companion input). */
  values: FormValues;
  form: VerbFormControl;
}

/** Builds the save from the form (default: 13-20's engine for its five verbs). */
export type VerbBuilder = (row: RecordRow, input: VerbBuildInput, now: Date) => RecordVerbBuild;

/** What a panel adds to the save: sibling rows, a reason it collects itself, and the gate's facts. */
export interface VerbExtraInputs {
  rows?: readonly SiblingInput[];
  reason?: string;
  /** The authority gate's facts (`notice` for the notification verb); the preparer is read from `siblings`. */
  ctx?: RecordAuthorityContext;
}

/** What `onSaved` hears. */
export interface VerbSaved {
  verb: RecordVerbKey;
  ref: string;
  mode: WriteMode;
  /** Sibling rows still owed (persisted, posted later): "N audit rows will be posted", never "done". */
  auditPending: number;
  /** 13.2-10: rows this save owes ANOTHER record's trail (the kept record's Note), still to be posted. */
  otherPending?: { ref: string; count: number };
}

/** 13.2-10: what `afterCommit` reports: rows owed on another record's trail (0 when they all posted). */
export interface VerbAfterCommit {
  ref: string;
  count: number;
}

export interface VerbFormProps {
  register: RecordRegister;
  verb: RecordVerbKey;
  row: RecordRow;
  siblings: readonly SiblingRow[];
  actor: Actor;
  /** The columns to show, in order (default: the verb's own inputs, `verbInputColumns`). */
  columns?: readonly string[];
  extraInputs?: VerbExtraInputs;
  /** Default: `buildRecordVerb` (13-20). A closure panel passes its 13-21 builder. */
  build?: VerbBuilder;
  /** Shown above the fields (e.g. "Client id to add"). */
  lead?: ReactNode;
  /** The Save button's words (default "Save"). */
  saveLabel?: string;
  /** `solemn` = the charcoal save, for a step that ends or reopens a record's life (D-13-02-1's language). */
  saveTone?: 'go' | 'solemn';
  /** Inputs that are not columns, shown after the columns (13-26). */
  extraFields?: readonly VerbExtraField[];
  /** Always ask for a reason, labelled so (13-26). */
  reasonPrompt?: VerbReasonPrompt;
  /** A note in the confirm, from the built patch (13-26: the open Closure Links, a withdrawal). */
  confirmNote?: (patch: RecordPatch) => ReactNode;
  /** Warning codes the confirm doesn't list one by one (the `confirmNote` says them). */
  quietWarnings?: readonly string[];
  /** Where a refused column that isn't on this form is recorded ("Record it under Handling first."). */
  elsewhere?: (column: string) => string | null;
  /** 13-27: columns that start blank whatever the record holds, so nothing is pre-selected (D-13-02-1). */
  unprefilled?: readonly string[];
  /** 13-27: a control the panel draws itself for a column (`null` = the default control). */
  renderControl?: (column: string, control: VerbControlProps) => ReactNode | null;
  /** 13-27: whether a column shows for the current answers. A hidden column is neither shown nor sent. */
  showColumn?: (column: string, values: FormValues) => boolean;
  /** 13-27: a live block after the fields. */
  afterFields?: (values: FormValues, control: VerbFormControl) => ReactNode;
  /** 13-27: a note under one field. */
  fieldNote?: (column: string, values: FormValues, control: VerbFormControl) => ReactNode;
  /** 13-27: start values where the record is blank. They are sent like a typed answer. */
  defaults?: Readonly<Record<string, string>>;
  /** 13-28: a block before one field (the WHS panel's group headings, one question at a time). */
  beforeField?: (column: string, values: FormValues) => ReactNode;
  /**
   * 13.2-09: the save can't go through yet for a reason the panel shows itself (triage on an incomplete report:
   * "Complete the report first"). The fields stay editable; "Review changes" is disabled.
   */
  saveBlocked?: boolean;
  /** 13.2-10: an extra input changed (as typed), for a panel that reads something from it. */
  onExtraChange?: (key: string, value: string) => void;
  /** 13.2-10: when this changes, the form goes back to editing (nothing reviewed); the inputs are kept. */
  resetKey?: string;
  /**
   * 13.2-10: after the save landed, post what the build owes another record. Given the whole build (a verb's own
   * extras, such as `closeAsDuplicate`'s `keptSiblings`). It must not throw; if it does, the save still stands.
   */
  afterCommit?: (build: Extract<RecordVerbBuild, { ok: true }>) => Promise<VerbAfterCommit | null>;
  onSaved: (saved: VerbSaved) => void;
}

type Phase = 'edit' | 'confirm' | 'saving' | 'saved';

interface Built {
  result: VerbResult;
  /** 13.2-10: the whole build, for `afterCommit`. */
  full: Extract<RecordVerbBuild, { ok: true }>;
  lines: ChangeLine[];
  warnings: Refusal[];
}

interface Conflict {
  /** `null` when the record was deleted (a 404). */
  current: RecordRow | null;
  columns: string[];
}

const fmt = (n: number) => n.toLocaleString('en-AU');

/** The record's values in the inputs' shape; an `unprefilled` column starts blank (13-27). */
function startValues(register: RecordRegister, row: RecordRow, cols: readonly string[], unprefilled: readonly string[] | undefined): Record<string, string> {
  const start = prefill(register, row, cols);
  for (const column of unprefilled ?? []) if (column in start) start[column] = '';
  return start;
}

const REASON_CODES: ReadonlySet<string> = new Set(['clear-needs-reason', 'change-needs-reason']);

function defaultBuilder(verb: RecordVerbKey, actor: Actor): VerbBuilder {
  if (!(ENGINE_VERBS as readonly string[]).includes(verb)) {
    throw new Error(`VerbForm: ${verb} needs a builder (only the 13-20 engine's verbs have a default)`);
  }
  return (row, input, now) =>
    buildRecordVerb(
      verb as EngineVerb,
      row,
      { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) },
      { set: input.set, ...(input.rows ? { rows: input.rows } : {}), ...(input.reason ? { reason: input.reason } : {}), auditId: input.auditId },
      now,
    );
}

/** The echo beside a date: `D MMM YYYY`, with the Melbourne time when one is entered. */
function dateEcho(day: string, time: string | undefined): string | null {
  if (!day) return null;
  if (time !== undefined) {
    if (!time) return `${formatDay(day)}; add the time.`;
    const r = melbourneWallToInstant(day, time);
    return r.ok ? formatInstant(r.value) : null;
  }
  const label = formatDay(day);
  return label === '—' ? null : label;
}

export default function VerbForm({
  register,
  verb,
  row,
  siblings,
  actor,
  columns,
  extraInputs,
  build,
  lead,
  saveLabel = 'Save',
  saveTone = 'go',
  extraFields,
  reasonPrompt,
  confirmNote,
  quietWarnings,
  elsewhere,
  unprefilled,
  renderControl,
  showColumn,
  afterFields,
  fieldNote,
  defaults,
  beforeField,
  saveBlocked = false,
  onExtraChange,
  resetKey,
  afterCommit,
  onSaved,
}: VerbFormProps) {
  const { writer, io } = useActionRuntime();
  const uid = useId();
  const cols = useMemo(() => [...(columns ?? verbInputColumns(register, verb))], [columns, register, verb]);
  const builder = useMemo(() => build ?? defaultBuilder(verb, actor), [build, verb, actor]);

  // The form starts from the record it was opened on. A conflict re-bases the save on the fresh record, never the inputs.
  // 13-27: an `unprefilled` column starts blank (nothing pre-selected); a `defaults` value fills a blank input only.
  const [initial] = useState<FormValues>(() => startValues(register, row, cols, unprefilled));
  const [values, setValues] = useState<Record<string, string>>(() => {
    const start = { ...startValues(register, row, cols, unprefilled) };
    for (const [column, value] of Object.entries(defaults ?? {})) if (!start[column]) start[column] = value;
    return start;
  });
  const [base, setBase] = useState<RecordRow>(row);
  const [reason, setReason] = useState('');
  const [extras, setExtras] = useState<Record<string, string>>(() =>
    Object.fromEntries((extraFields ?? []).map((f) => [f.key, f.initial ?? ''])),
  );
  const [phase, setPhase] = useState<Phase>('edit');
  const [built, setBuilt] = useState<Built | null>(null);
  const [auditId, setAuditId] = useState<string | null>(null);
  const [refusals, setRefusals] = useState<Refusal[]>([]);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [saved, setSaved] = useState<VerbSaved | null>(null);
  const [today] = useState(() => todayMelbourne(new Date()));

  // 13.2-10: something the form doesn't own changed (the kept record's read): back to editing, the inputs kept.
  const [seenResetKey, setSeenResetKey] = useState(resetKey);
  if (seenResetKey !== resetKey) {
    setSeenResetKey(resetKey);
    if (phase === 'confirm') {
      setPhase('edit');
      setBuilt(null);
    }
  }

  const busy = phase === 'saving';
  const ready = io !== null;
  const idOf = (column: string) => `${uid}-${column}`;

  // 13-33: after a review that refused, the first refused input (in form order) takes the focus, so it is fixed at
  // once (the lodge's rule, 13-24). The id is resolved after the render that shows the field (the reason box, say).
  const focusNext = useRef<string | null>(null);
  useEffect(() => {
    const id = focusNext.current;
    if (id === null) return;
    focusNext.current = null;
    document.getElementById(id)?.focus();
  });

  const refusalAt = useMemo(() => {
    const m = new Map<string, string>();
    for (const r of refusals) if (r.column && !m.has(r.column)) m.set(r.column, r.message);
    return m;
  }, [refusals]);
  const warningAt = useMemo(() => new Map((built?.warnings ?? []).filter((w) => w.column).map((w) => [w.column as string, w.message])), [built]);
  const conflictAt = useMemo(() => {
    const m = new Map<string, string>();
    if (!conflict?.current) return m;
    for (const c of conflict.columns) {
      if (cols.includes(c)) m.set(c, displayValue(specOf(register, c), conflict.current.fields[c]));
    }
    return m;
  }, [conflict, cols, register]);

  // 13-27: the columns the current answers show; only these are rendered, checked and sent.
  const shown = showColumn ? cols.filter((c) => showColumn(c, values)) : cols;
  const cleared = clearedColumns(shown, initial, values);
  const needsReason =
    reasonPrompt !== undefined || cleared.length > 0 || refusals.some((r) => REASON_CODES.has(r.code) || r.column === 'reason');
  const extraColumns = useMemo(() => new Set((extraFields ?? []).map((f) => f.column ?? f.key)), [extraFields]);
  const stamps = stampLabels(register, verb);

  function edit(column: string, value: string) {
    const baseColumn = column.replace(/@time$/, '');
    setValues((v) => ({ ...v, [column]: value }));
    setRefusals((rs) => rs.filter((r) => r.column !== baseColumn));
    if (phase !== 'edit') setPhase('edit');
    setBuilt(null);
    setSaved(null);
  }

  /** 13-27: something outside the inputs changed (the override): back to editing, nothing reviewed. */
  function invalidate() {
    if (phase !== 'edit') setPhase('edit');
    setBuilt(null);
    setSaved(null);
  }

  const control: VerbFormControl = {
    set: (column, value) => edit(column, value),
    invalidate,
    refusal: (key) => refusalAt.get(key),
  };

  function editExtra(field: VerbExtraField, value: string) {
    setExtras((x) => ({ ...x, [field.key]: value }));
    onExtraChange?.(field.key, value);
    setRefusals((rs) => rs.filter((r) => r.column !== (field.column ?? field.key)));
    if (phase !== 'edit') setPhase('edit');
    setBuilt(null);
    setSaved(null);
  }

  /** The input id of the first refusal in form order: the columns shown, then the extra inputs, then the reason. */
  function firstRefusedId(list: readonly Refusal[]): string | null {
    const at = new Set(list.map((r) => r.column).filter((c): c is string => typeof c === 'string'));
    for (const column of shown) if (at.has(column)) return idOf(column);
    for (const f of extraFields ?? []) if (at.has(f.column ?? f.key)) return idOf(`x-${f.key}`);
    if (at.has('reason')) return idOf('reason');
    return null;
  }

  /** Build the save (pure); show the refusals, or the confirm. Nothing is sent. */
  function review() {
    setFailure(null);
    setSaved(null);
    const id = crypto.randomUUID();
    const own = formRefusals(register, shown, values, actor.upn, initial);
    const why = (extraInputs?.reason ?? reason).trim();
    const result = builder(
      base,
      {
        set: changedSet(register, shown, initial, values),
        ...(extraInputs?.rows ? { rows: extraInputs.rows } : {}),
        ...(why ? { reason: why } : {}),
        ...(extraFields && extraFields.length > 0 ? { extra: { ...extras } } : {}),
        auditId: id,
      },
      new Date(),
    );
    const all = [...own, ...(result.ok ? [] : result.refusals)];
    if (all.length > 0 || !result.ok) {
      setRefusals(all);
      setBuilt(null);
      setPhase('edit');
      focusNext.current = firstRefusedId(all);
      return;
    }
    setRefusals([]);
    setAuditId(id);
    setBuilt({
      result: { patch: result.patch, siblings: result.siblings },
      full: result,
      lines: changeLines(register, base, result.patch),
      warnings: result.warnings,
    });
    setPhase('confirm');
  }

  /** Save through the Writer (synchronous, If-Match). */
  async function save() {
    if (!built || !io || !auditId) return;
    setPhase('saving');
    setFailure(null);
    try {
      const r = await writer.commitRecordVerb(io as RecordIO, base.ref, base.eTag, built.result, actor, {
        verb,
        row: base,
        ctx: {
          ...(extraInputs?.ctx ?? {}),
          preparedByUpn: preparedByUpn(siblings),
          columns: Object.keys(built.result.patch),
        },
      });
      // 13.2-10: rows owed on another record, after the save landed. A failure here never undoes the save.
      let other: VerbAfterCommit | null = null;
      if (afterCommit) {
        try {
          other = await afterCommit(built.full);
        } catch (err) {
          console.warn('[record] rows for another record could not be posted:', err);
        }
      }
      const info: VerbSaved = {
        verb,
        ref: base.ref,
        mode: writer.mode,
        auditPending: r.auditPending.length,
        ...(other && other.count > 0 ? { otherPending: other } : {}),
      };
      setSaved(info);
      setConflict(null);
      setBuilt(null);
      setPhase('saved');
      onSaved(info);
    } catch (err) {
      if (err instanceof RecordConflictError) {
        setConflict({ current: err.current, columns: [...new Set([...err.columns, ...err.changedSinceRead])] });
        if (err.current) setBase(err.current);
        setBuilt(null);
        setPhase('edit');
      } else if (err instanceof RecordRefusedError) {
        setRefusals([{ code: err.reason, message: RECORD_AUTHORITY_TEXT[err.reason] }]);
        setBuilt(null);
        setPhase('edit');
      } else {
        setFailure(err instanceof Error ? err.message : 'The save did not go through.');
        setPhase('confirm');
      }
    }
  }

  // --- Fields --------------------------------------------------------------------------------------------------
  function renderField(column: string) {
    const spec = specOf(register, column);
    const id = idOf(column);
    const value = values[column] ?? '';
    const message = refusalAt.get(column);
    const warning = warningAt.get(column);
    const fresh = conflictAt.get(column);
    const describedBy = [message ? `${id}-msg` : null, fresh !== undefined ? `${id}-fresh` : null].filter(Boolean).join(' ') || undefined;
    const refusedClass = message ? styles.inputRefused : undefined;
    const label = (
      <label htmlFor={id} className={styles.label}>
        {spec.label}
      </label>
    );
    const notes = (
      <>
        {fresh !== undefined && (
          <p id={`${id}-fresh`} className={styles.fresh}>
            Now on the record: <strong>{fresh}</strong>
          </p>
        )}
        {message && (
          <p id={`${id}-msg`} className={styles.refusal}>
            {message}
          </p>
        )}
        {warning && !message && <p className={styles.notice}>{warning}</p>}
        {fieldNote?.(column, values, control)}
      </>
    );

    const own = renderControl?.(column, {
      id,
      value,
      set: (v) => edit(column, v),
      refused: !!message,
      values,
      form: control,
      ...(describedBy ? { describedBy } : {}),
    });
    if (own) {
      return (
        <div key={column} className={styles.field}>
          {label}
          {own}
          {notes}
        </div>
      );
    }

    if (spec.kind === 'choice') {
      const live = choicesOf(register, column);
      const stray = value !== '' && !live.includes(value);
      return (
        <div key={column} className={styles.field}>
          {label}
          <Select id={id} value={value} className={refusedClass} aria-describedby={describedBy} onChange={(e) => edit(column, e.target.value)}>
            <option value="">Not answered</option>
            {stray && <option value={value}>{value} (not a current choice)</option>}
            {live.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </Select>
          {notes}
        </div>
      );
    }

    if (spec.kind === 'day-new' || spec.kind === 'day-existing' || spec.kind === 'instant') {
      // 13.2-09: a `timeWhenKnown` day (OccurredOn, ReportedOn) shows an optional time, as the lodge form does.
      const timed = takesTime(spec);
      const optionalTime = timed && spec.kind !== 'instant';
      const tKey = timeKeyOf(column);
      const time = values[tKey] ?? '';
      const echo = optionalTime
        ? value
          ? `${dateEcho(value, time.trim() ? time : undefined) ?? formatDay(value)}${time.trim() ? '' : '. Add the time if it is known (Melbourne).'}`
          : null
        : dateEcho(value, timed ? time : undefined);
      return (
        <div key={column} className={styles.field}>
          {label}
          <div className={styles.dateRow}>
            <Input
              id={id}
              type="date"
              value={value}
              className={`${styles.dateInput} ${refusedClass ?? ''}`}
              aria-describedby={[describedBy, `${id}-echo`].filter(Boolean).join(' ')}
              onChange={(e) => edit(column, e.target.value)}
            />
            {timed && (
              <Input
                id={`${id}-time`}
                type="time"
                value={time}
                className={styles.timeInput}
                aria-label={`${spec.label}: time`}
                onChange={(e) => edit(tKey, e.target.value)}
              />
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => edit(column, today)}>
              Today
            </Button>
          </div>
          <p id={`${id}-echo`} className={styles.hint}>
            {echo ?? (optionalTime ? 'Enter the date, and the time if it is known (Melbourne).' : timed ? 'Enter the date and the time (Melbourne).' : NOT_RECORDED)}
          </p>
          {notes}
        </div>
      );
    }

    const cap = spec.cap ?? 255;
    const over = value.length > cap;
    const count = (
      <span className={`${styles.count} ${over ? styles.countOver : ''}`} aria-live="polite">
        {fmt(value.length)} / {fmt(cap)}
      </span>
    );

    if (spec.kind === 'textarea') {
      return (
        <div key={column} className={styles.field}>
          {label}
          <TextArea id={id} rows={4} value={value} className={refusedClass} aria-describedby={describedBy} onChange={(e) => edit(column, e.target.value)} />
          <div className={styles.under}>{count}</div>
          {over && <p className={styles.notice}>That's over the {fmt(cap)}-character limit. It won't be cut short; please shorten it.</p>}
          {notes}
        </div>
      );
    }

    const person = spec.kind === 'upn';
    return (
      <div key={column} className={styles.field}>
        {label}
        <div className={person ? styles.inline : undefined}>
          <Input
            id={id}
            type={person ? 'email' : 'text'}
            value={value}
            className={refusedClass}
            aria-describedby={describedBy}
            placeholder={person ? 'Their Harmony email address' : undefined}
            onChange={(e) => edit(column, e.target.value)}
          />
          {person && (
            <Button type="button" variant="secondary" size="sm" onClick={() => edit(column, actor.upn)}>
              Me
            </Button>
          )}
        </div>
        {spec.kind === 'position' && <p className={styles.hint}>A role, not a name.</p>}
        {(over || value.length > cap * 0.8) && <div className={styles.under}>{count}</div>}
        {notes}
      </div>
    );
  }

  const placed = (column: string | undefined) =>
    column !== undefined &&
    (cols.includes(column) || extraColumns.has(column) || (column === 'reason' && needsReason && extraInputs?.reason === undefined));
  const unplaced = refusals.filter((r) => !placed(r.column));
  const shownWarnings = (built?.warnings ?? []).filter((w) => !quietWarnings?.includes(w.code));

  function renderExtra(field: VerbExtraField) {
    const id = idOf(`x-${field.key}`);
    const value = extras[field.key] ?? '';
    const message = refusalAt.get(field.column ?? field.key);
    const echo = field.kind === 'day' ? dateEcho(value, undefined) : null;
    const hint = [echo, field.hint].filter(Boolean).join(' · ');
    const describedBy = [message ? `${id}-msg` : null, hint ? `${id}-hint` : null].filter(Boolean).join(' ') || undefined;
    const refusedClass = message ? styles.inputRefused : undefined;
    return (
      <div key={`x-${field.key}`} className={styles.field}>
        <label htmlFor={id} className={styles.label}>
          {field.label}
        </label>
        {field.kind === 'text' ? (
          <Input
            id={id}
            type="text"
            value={value}
            autoComplete="off"
            spellCheck={false}
            className={refusedClass}
            aria-describedby={describedBy}
            onChange={(e) => editExtra(field, e.target.value)}
          />
        ) : field.kind === 'day' ? (
          <div className={styles.dateRow}>
            <Input
              id={id}
              type="date"
              value={value}
              className={`${styles.dateInput} ${refusedClass ?? ''}`}
              aria-describedby={describedBy}
              onChange={(e) => editExtra(field, e.target.value)}
            />
            <Button type="button" variant="ghost" size="sm" onClick={() => editExtra(field, today)}>
              Today
            </Button>
          </div>
        ) : (
          <TextArea id={id} rows={3} value={value} className={refusedClass} aria-describedby={describedBy} onChange={(e) => editExtra(field, e.target.value)} />
        )}
        {hint && (
          <p id={`${id}-hint`} className={styles.hint}>
            {hint}
          </p>
        )}
        {message && (
          <p id={`${id}-msg`} className={styles.refusal}>
            {message}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.verbForm}>
      {lead}
      {conflict && (
        <div className={styles.conflict} role="alert">
          {conflict.current ? (
            <p className={styles.summaryTitle}>Someone else changed this record. Check and save again.</p>
          ) : (
            <p className={styles.summaryTitle}>This record can't be found any more. It may have been deleted.</p>
          )}
          {conflict.current && conflictAt.size > 0 && (
            <p className={styles.hint}>What's on the record now is shown under each field that changed.</p>
          )}
        </div>
      )}
      {refusals.length > 0 && (
        <div className={styles.summary} role="alert">
          <p className={styles.summaryTitle}>
            {refusals.length === 1 ? 'One thing to fix before this can be saved:' : `${refusals.length} things to fix before this can be saved:`}
          </p>
          <ul className={styles.summaryList}>
            {refusals.map((r, i) => {
              const where = !placed(r.column) && r.column && elsewhere ? elsewhere(r.column) : null;
              return (
                <li key={`${r.code}-${r.column ?? ''}-${i}`}>
                  {r.message}
                  {where ? ` ${where}` : ''}
                </li>
              );
            })}
          </ul>
          {unplaced.length > 0 && unplaced.length < refusals.length && (
            <p className={styles.hint}>The rest are shown beside their fields.</p>
          )}
        </div>
      )}

      <fieldset className={styles.fields} disabled={busy} inert={busy || undefined}>
        {shown.map((c) =>
          beforeField ? (
            <Fragment key={c}>
              {beforeField(c, values)}
              {renderField(c)}
            </Fragment>
          ) : (
            renderField(c)
          ),
        )}
        {afterFields?.(values, control)}
        {(extraFields ?? []).map((f) => renderExtra(f))}
        {needsReason && extraInputs?.reason === undefined && (
          <div className={styles.field}>
            <label htmlFor={idOf('reason')} className={styles.label}>
              {reasonPrompt?.label ?? 'Why the change (goes on the audit trail)'}
            </label>
            {reasonPrompt?.hint && <p className={styles.hint}>{reasonPrompt.hint}</p>}
            <TextArea
              id={idOf('reason')}
              rows={2}
              value={reason}
              className={refusalAt.has('reason') ? styles.inputRefused : undefined}
              onChange={(e) => {
                setReason(e.target.value);
                setRefusals((rs) => rs.filter((r) => r.column !== 'reason' && !REASON_CODES.has(r.code)));
                setBuilt(null);
                if (phase !== 'edit') setPhase('edit');
              }}
            />
            {refusalAt.get('reason') && <p className={styles.refusal}>{refusalAt.get('reason')}</p>}
          </div>
        )}
      </fieldset>

      {stamps.length > 0 && <p className={styles.hint}>Set when you save: {stamps.join(', ')}.</p>}

      {phase === 'confirm' && built && (
        <div className={styles.confirm} role="status">
          <p className={styles.confirmTitle}>Check before you save</p>
          <ul className={styles.changeList}>
            {built.lines.map((l) => (
              <li key={l.column}>
                <span className={styles.changeLabel}>{l.label}:</span> {l.from} <span aria-hidden="true">→</span>
                <span className={styles.srOnly}> to </span> <strong>{l.to}</strong>
              </li>
            ))}
          </ul>
          {built.result.siblings.length > 0 && (
            <p className={styles.hint}>
              {built.result.siblings.length} audit {built.result.siblings.length === 1 ? 'row' : 'rows'} will be written under
              your name.
            </p>
          )}
          {confirmNote?.(built.result.patch)}
          {shownWarnings.length > 0 && (
            <ul className={styles.summaryList}>
              {shownWarnings.map((w, i) => (
                <li key={`${w.code}-${i}`}>{w.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {failure && (
        <p className={styles.failure} role="alert">
          Couldn't save that: {failure}
        </p>
      )}

      {phase === 'saved' && saved && (
        <p className={styles.saved} role="status">
          <span className={styles.tick} aria-hidden="true">
            ✓
          </span>{' '}
          {saved.auditPending > 0
            ? `Saved; ${saved.auditPending} audit ${saved.auditPending === 1 ? 'row' : 'rows'} will be posted.`
            : 'Saved'}
          {saved.otherPending &&
            ` ${saved.otherPending.count} ${saved.otherPending.count === 1 ? 'row' : 'rows'} for ${saved.otherPending.ref} will be posted.`}
          {saved.mode === 'practice' && <span className={styles.hint}> Practice mode: nothing was written to SharePoint.</span>}
        </p>
      )}

      <div className={styles.actions}>
        {!ready && <span className={styles.hint}>Connecting to SharePoint…</span>}
        {phase === 'confirm' || phase === 'saving' ? (
          <>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => setPhase('edit')}>
              Change it
            </Button>
            <Button
              type="button"
              loading={busy}
              disabled={!ready}
              className={saveTone === 'solemn' ? styles.solemn : undefined}
              onClick={() => void save()}
            >
              {saveLabel}
            </Button>
          </>
        ) : (
          <Button type="button" disabled={!ready || saveBlocked} onClick={review}>
            Review changes
          </Button>
        )}
      </div>
    </div>
  );
}
